import React from 'react';
import { Layout } from '../components/Layout';
import { usePageMetadata } from '../hooks/usePageMetadata';
import { 
  Camera, DollarSign, MessageSquare, Star, Shield, Calendar,
  CheckCircle, ArrowRight, Crown
} from 'lucide-react';
import { Link } from 'react-router-dom';

const OwnerGuidePage = () => {
  usePageMetadata();

  const steps = [
    {
      icon: Shield,
      title: "1. Create Your Account",
      content: "Sign up as a property owner. Verify your email and complete your profile with accurate information."
    },
    {
      icon: Crown,
      title: "2. Choose a Subscription",
      content: "Select from our Standard ($99/mo), Featured ($149/mo), or Concierge ($199/mo) plans based on your needs."
    },
    {
      icon: Camera,
      title: "3. Create Your Listing",
      content: "Add high-quality photos, write a compelling description, set your price, and specify amenities and house rules."
    },
    {
      icon: MessageSquare,
      title: "4. Respond to Inquiries",
      content: "Communicate with potential renters, answer questions, and schedule tours if requested."
    },
    {
      icon: Calendar,
      title: "5. Manage Bookings",
      content: "Review and approve booking requests. Set your calendar availability and manage reservations."
    },
    {
      icon: DollarSign,
      title: "6. Get Paid",
      content: "Receive secure payments directly to your bank account. No per-booking fees."
    }
  ];

  const tips = [
    {
      title: "Professional Photos Matter",
      content: "Properties with high-quality photos get 3x more inquiries. Use natural lighting and capture all rooms."
    },
    {
      title: "Price Competitively",
      content: "Research similar listings in your area. Consider including utilities to attract more renters."
    },
    {
      title: "Respond Quickly",
      content: "Fast response times lead to more bookings. Aim to reply within a few hours."
    },
    {
      title: "Be Detailed",
      content: "Include all amenities, nearby attractions, and house rules. Clear expectations prevent issues."
    },
    {
      title: "Highlight Unique Features",
      content: "What makes your property special? A garden, parking, workspace? Make it stand out."
    },
    {
      title: "Keep Your Calendar Updated",
      content: "Block unavailable dates promptly to avoid double bookings and disappointed renters."
    }
  ];

  return (
    <Layout>
      <div className="bg-gradient-to-b from-slate-50 to-white min-h-screen">
        {/* Hero */}
        <section className="py-16 px-4 bg-coral/5">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Property Owner Guide
            </h1>
            <p className="text-xl text-slate-600 mb-8">
              Everything you need to know to successfully list and manage your property on Stay Pair Homes.
            </p>
            <Link 
              to="/pricing"
              className="inline-flex items-center justify-center px-6 py-3 bg-coral text-white rounded-xl font-medium hover:bg-coral/90 transition-colors"
            >
              View Pricing Plans
              <ArrowRight className="w-4 h-4 ml-2" />
            </Link>
          </div>
        </section>

        {/* Steps */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Getting Started</h2>
            <div className="space-y-6">
              {steps.map((step, index) => (
                <div 
                  key={index}
                  className="flex gap-4 bg-white rounded-xl border border-slate-200 p-6"
                >
                  <div className="w-12 h-12 bg-coral/10 rounded-xl flex items-center justify-center shrink-0">
                    <step.icon className="w-6 h-6 text-coral" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-slate-600">{step.content}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tips */}
        <section className="py-16 px-4 bg-slate-50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-12 text-center">Tips for Success</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tips.map((tip, index) => (
                <div 
                  key={index}
                  className="bg-white rounded-xl border border-slate-200 p-6"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 shrink-0" />
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-2">{tip.title}</h3>
                      <p className="text-slate-600 text-sm">{tip.content}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Subscription Plans Preview */}
        <section className="py-16 px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Subscription Plans</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Standard", price: "$99", features: ["1 Active Listing", "Basic Support", "Standard Placement"] },
                { name: "Featured", price: "$149", features: ["3 Active Listings", "Priority Support", "Featured Placement"], popular: true },
                { name: "Concierge", price: "$199", features: ["Unlimited Listings", "Dedicated Support", "Top Placement"] }
              ].map((plan, index) => (
                <div 
                  key={index}
                  className={`rounded-xl border p-6 ${plan.popular ? 'border-trust-blue bg-trust-blue/5' : 'border-slate-200 bg-white'}`}
                >
                  {plan.popular && (
                    <span className="inline-block bg-trust-blue text-white text-xs px-2 py-1 rounded mb-3">
                      Most Popular
                    </span>
                  )}
                  <h3 className="font-bold text-slate-900 text-lg">{plan.name}</h3>
                  <p className="text-3xl font-bold text-slate-900 mt-2">
                    {plan.price}<span className="text-base font-normal text-slate-500">/mo</span>
                  </p>
                  <ul className="mt-4 space-y-2">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-slate-600">
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
            <div className="text-center mt-8">
              <Link 
                to="/pricing"
                className="text-trust-blue hover:underline font-medium"
              >
                View full plan comparison →
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 px-4 bg-slate-900 text-white">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to List Your Property?</h2>
            <p className="text-slate-300 mb-8">
              Join hundreds of property owners already using Stay Pair Homes.
            </p>
            <Link 
              to="/register"
              className="inline-flex items-center justify-center px-8 py-4 bg-coral text-white rounded-xl font-medium hover:bg-coral/90 transition-colors"
            >
              Get Started Today
            </Link>
          </div>
        </section>
      </div>
    </Layout>
  );
};

export default OwnerGuidePage;
